import React, { useState } from 'react';
import {
  GeckoFields,
  GeckoTable,
  GeckoPagination,
  GECKO_UTILS,
  GECKO_CONSTS,
  GeckoAlert,
} from 'ui';
import GeckoRequest from 'services/gecko-request';
import PaginationService from 'services/gecko-pagination';
import AREA_CODES from 'constants/areaCodes';
import Loader from 'components/Loader';
import { renderError } from 'services/gecko-error';
import SearchField from 'components/SearchField';
import Phone from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './TwilioChannelForm.scss';

const twilioCountryCodes = [
  { label: 'Great Britain', value: 'GB' },
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
];

function TwilioChannelForm({ value, onChange, disableForwarding }) {
  const [allAvailableNumbers, setAvailableNumbers] = useState([]);
  const [searchingForNumbers, setSearchingForNumbers] = useState(false);
  const [page, setPage] = useState(1);

  const searchNumbers = (value) => {
    setSearchingForNumbers(true);
    const { country_code, area_code, type, search } = value.configuration;
    return GeckoRequest.get('/channels/twilio/available-numbers', {
      country_code,
      area_code,
      type,
      search,
    })
      .then((phoneNumbers) => {
        setSearchingForNumbers(false);
        setAvailableNumbers(phoneNumbers);
      })
      .catch((err) => {
        renderError(err);
        setSearchingForNumbers(false);
      });
  };

  const changeHandler = (key, subValue) => {
    let newValue = GECKO_UTILS.setNestedValue(value, key, subValue);
    if (
      key === 'configuration.country_code' &&
      (subValue !== 'US' || subValue !== 'CA')
    ) {
      newValue.configuration.area_code = null;
    }
    if (key === 'configuration.country_code' && subValue === 'GB') {
      newValue.configuration.type = 'mobile';
    }
    if (key === 'configuration.country_code' && subValue !== 'GB') {
      newValue.configuration.type = 'local';
    }
    if (
      key === 'configuration.country_code' ||
      key === 'configuration.area_code' ||
      key === 'configuration.type'
    ) {
      newValue.configuration.twilio_number = null;
      newValue.configuration.search = null;
      searchNumbers(newValue);
    }
    if (key === 'configuration.search') {
      searchNumbers(newValue);
    }
    onChange(newValue);
  };

  const fields = [
    {
      key: 'name',
      type: GECKO_CONSTS.FIELD_TYPE_TEXT,
      label: 'Channel Title',
      width: 'col-xs-12',
      required: true,
    },
    {
      key: 'configuration.country_code',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      label: 'Country',
      width: 'col-xs-12',
      options: twilioCountryCodes,
      disabledWhen: () => value.configuration.phone_number,
      required: true,
    },
    {
      key: 'configuration.area_code',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      label: 'Area Code',
      width: 'col-xs-12',
      options: AREA_CODES[value.configuration.country_code],
      disabledWhen: () => value.configuration.phone_number,
      hideWhen: () =>
        value.configuration.country_code !== 'US' &&
        value.configuration.country_code !== 'CA',
    },
    {
      key: 'configuration.search',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      description: `A pattern on which to match the available phone numbers. Valid characters are '*', 0-9 and a-z. The '*' character will match any single digit. e.g. '510555****'`,
      label: 'Search',
      width: 'col-xs-12',
      component: <SearchField />,
      disabledWhen: () => value.configuration.phone_number,
      hideWhen: () =>
        !value.configuration.country_code || value.configuration.phone_number,
    },
    {
      key: 'configuration.phone_number',
      type: GECKO_CONSTS.FIELD_TYPE_TELEPHONE,
      label: 'Phone Number',
      width: 'col-xs-12',
      disabled: true,
      hideWhen: () =>
        !value.configuration.phone_number || value.configuration.twilio_number,
    },
    {
      key: 'configuration.twilio_number.friendly_name',
      type: GECKO_CONSTS.FIELD_TYPE_TELEPHONE,
      label: 'Phone Number',
      width: 'col-xs-12',
      disabled: true,
      hideWhen: () => !value.configuration.twilio_number,
    },
    !disableForwarding && {
      key: 'configuration.forwarding_number',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: <Phone placeholder="Enter phone number" />,
      label: 'Forward Incoming Calls to',
      width: 'col-xs-12',
      description:
        'Forward any calls to this number to an alternate number, leave this field blank to not forward calls',
      hideWhen: () =>
        !value.configuration.phone_number && !value.configuration.twilio_number,
    },
  ];

  const pickNumber = (number) => {
    onChange(
      GECKO_UTILS.setNestedValue(value, 'configuration.twilio_number', number)
    );
  };

  const cols = [
    {
      key: 'friendly_name',
      name: 'Choose a number',
      width: '100%',
    },
  ];

  const [availableNumbers, pagination] = PaginationService.paginate({
    page: page,
    changePage: setPage,
    per_page: 8,
  })(allAvailableNumbers);

  const shouldShowNumbers =
    value.configuration.country_code &&
    !value.configuration.twilio_number &&
    !value.configuration.phone_number;

  return (
    <div className="TwillioChannelForm">
      <GeckoFields fields={fields} values={value} handler={changeHandler} />
      {shouldShowNumbers ? (
        <Loader isLoading={searchingForNumbers}>
          <div className="TwilioChannelForm-numbers-table">
            <GeckoTable
              cols={cols}
              rows={availableNumbers}
              rowHandler={pickNumber}
            />
            {!searchingForNumbers &&
            (!availableNumbers || !availableNumbers.length) ? (
              <GeckoAlert type="warning">
                No available phone numbers have been found for the provided
                criteria.
              </GeckoAlert>
            ) : (
              <GeckoPagination pagination={pagination} />
            )}
          </div>
        </Loader>
      ) : null}
    </div>
  );
}

export default TwilioChannelForm;
